import React, { useEffect } from 'react';
import { Card, Row, Col, ListGroup, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';

const Profile = () => {
  const { currentUser, loading, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  
  // Use useEffect to handle redirection after loading is complete
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      console.log("User not authenticated, redirecting to login");
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);
  
  // Don't render anything while checking authentication
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </div>
    );
  }

  // Safety check for currentUser - this shouldn't happen if protected routes work correctly
  if (!currentUser) {
    return (
      <Alert variant="warning">
        No se pudo cargar la información del usuario. Por favor, intenta nuevamente.
        <div className="text-center mt-3">
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Recargar página
          </button>
        </div>
      </Alert>
    );
  }

  // Safe getter for profile information
  const getProfileInfo = (field) => {
    try {
      return currentUser[field] || "No disponible";
    } catch (error) {
      logger.error(`Error accessing field ${field}:`, error);
      return "No disponible";
    }
  };

  // Safe getter for initial letter
  const getInitial = () => {
    try {
      if (currentUser && currentUser.full_name) {
        return currentUser.full_name.charAt(0).toUpperCase();
      }
      return "?";
    } catch (error) {
      return "?";
    }
  };

  return (
    <div className="profile-container">
      <Card>
        <Card.Header as="h5" className="bg-primary text-white">
          Perfil de Usuario
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4} className="text-center mb-3 mb-md-0">
              <div className="profile-image-container">
                {/* Imagen de perfil circular (opcional) */}
                <div 
                  className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center mx-auto mb-3" 
                  style={{ width: '100px', height: '100px', fontSize: '2.5rem' }}
                >
                  {getInitial()}
                </div>
                <h5>{getProfileInfo('full_name')}</h5>
                
                {/* Botón de cerrar sesión */}
                <button 
                  className="btn btn-outline-danger mt-3"
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                >
                  <i className="bi bi-box-arrow-right me-2"></i>
                  Cerrar Sesión
                </button>
              </div>
            </Col>
            <Col md={8}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>Email:</strong> {getProfileInfo('email')}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Teléfono:</strong> {getProfileInfo('phone_number')}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Estado de cuenta:</strong> {getProfileInfo('is_active') === true ? 'Activa' : 'Inactiva'}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Fecha de registro:</strong> {
                    getProfileInfo('created_at') !== 'No disponible' 
                      ? new Date(getProfileInfo('created_at')).toLocaleDateString() 
                      : 'No disponible'
                  }
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer className="text-muted">
          <small>Última actualización: {
            getProfileInfo('updated_at') !== 'No disponible' 
              ? new Date(getProfileInfo('updated_at')).toLocaleString() 
              : 'Sin actualizaciones'
          }</small>
        </Card.Footer>
      </Card>
      
      <Card className="mt-4">
        <Card.Header as="h5" className="bg-primary text-white">
          Instrucciones de WhatsApp
        </Card.Header>
        <Card.Body>
          <h6 className="mb-3">Para interactuar con nuestro servicio mediante WhatsApp:</h6>
          <ol>
            <li className="mb-2">Agrega nuestro número de WhatsApp oficial a tus contactos.</li>
            <li className="mb-2">Envía un mensaje para comenzar una conversación.</li>
            <li className="mb-2">Utiliza los siguientes comandos especiales para acciones específicas:
              <ul className="mt-2">
                <li><strong>#ayuda</strong> - Muestra todos los comandos disponibles</li>
                <li><strong>#login</strong> - Recibe un enlace para iniciar sesión</li>
                <li><strong>#registro</strong> - Recibe un enlace para registrarte</li>
                <li><strong>#perfil</strong> - Consulta tu información de perfil</li>
              </ul>
            </li>
            <li className="mb-2">Para preguntas sobre normativas de construcción, simplemente escribe tu consulta directamente.</li>
          </ol>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Profile;